/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */
import styles from './cell.module.css' with { type: 'css' };
import miniTheme from './mini.module.css' with { type: 'css' };
import tutorialTheme from './tutorial.module.css' with { type: 'css' };
import cssDefs from '../definitions.module.css' with { type: 'css' }; 
import { ContextMenuStyles } from '../components';
import html from './cell.module.html';

import { PythonEditor } from '../codemirror';
import { MusicKey, SoundLoader, toStr } from '../audio';
import { Synthesizer, Note, TunePadAudio, MIDIManager } from '../audio';
import { MusicTrace, TraceEvent } from '../audio';

import { showBPMDialog } from './dialogs/bpm-dialog';
import { showTimeDialog } from './dialogs/time-dialog';
import { showKeyDialog } from './dialogs/key-dialog';

import { PythonRuntime, PythonError, CompileResponse } from '../compiler/runtime';
import { Timeline, TimelineViews } from './timeline/timeline';
import { TunePadInstrument } from './instrument';
import { ProjectOutline } from './outline/outline';


/**
 * ## TunePad code cell.
 * ```html
 * <tunepad-cell
 *    uuid = "{{ database UUID }}"
 *    name = "{{ valid python file }}"
 *    saved = "{{ date }}"
 *    readonly = "false"
 *    autocompile = "false"
 *    theme = "dark | light"
 *    timeline = "midi" | "waveform" | "score" | "canvas" | "none"
 *    patch = "grand-piano"
 *    canvas = "{{ canvas name }}"
 *    host = "https://api.tunepad.com"
 *    font-size = "13px"
 *    midi-out = "false"
 *    [ show-instrument = "false" ] 
 *    mini-in = "false">
 * playNote(30)  # default Python code here...
 * </tunepad-cell>
 * ```
 */
export class TunePadCell extends HTMLElement {

    static readonly ELEMENT = "tunepad-cell";

    static observedAttributes = [
        "uuid",        // REQUIRED database ID (should never change)
        "name",        // REQUIRED page-unique id for this code snippet (must be valid python identifer)
        "saved",       // when this cell was last saved to the datastore
        "readonly",    // boolean string, default is false
        "autocompile", // boolean (compile on line edits, default is false)
        "theme",       // "dark" | "light" (default is dark)
        "timeline",    // timeline view to show ("midi", "waveform", "score", "canvas", or "none")
        "patch",       // synthesizer patch name (e.g "grand-piano", "acoustic-guitar")
        "canvas",      // name of a <tunepad-canvas> element
        "host",        // datastore host URL for loading custom sounds (https://api.tunepad.com)
        "font-size",   // python editor font size
        "midi-out",    // generate midi output
        "midi-in",     // listen for midi input
    ];

    /** unique database identifier */
    private _uuid : string = self.crypto.randomUUID();

    /** REQUIRED attribute -- unique database identifier */
    get uuid() : string { return this._uuid; }

    /** python "file" name. should be a legal and unique python identifier */
    name : string = 'tunepad_cell';

    /** contents of the code editor */
    get code() { return this.editor.code; }

    /** canvas attribute */
    get canvas() : string {
        // if the timeline view is set to canvas, this will return
        // the uuid for the canvas.
        const c = this.timeline.querySelector('timeline-canvas canvas');
        return c?.getAttribute('data-uuid') ?? '';
    }

    /** all of the HTML elements are contained within a shadow DOM */
    private root : ShadowRoot;

    /** <tunepad-python-editor> element */
    private editor : PythonEditor;

    /** <tunepad-timeline> element */
    private timeline : Timeline;
    
    /** name input field */
    private nameField : HTMLInputElement;

    /** automatically compile after line edits */
    private autocompile : boolean = false;

    /** generate midi output from the keyboard */
    private midiOut : boolean = false;

    /** listen for incoming midi events */
    private midiIn : boolean = false;

    /** code changed */
    private codeChanged : boolean = false;

    /** which virtual instrument to show (piano, bass, guitar, drums, definitions) */
    private _instrument : string = 'definitions';
    public get instrument() {
        if (this._instrument === 'definitions' && this.getAttribute('timeline') === 'canvas') {
            return 'canvas';
        } else {
            return this._instrument;
        }
    }

    /** patch name (e.g. 'grand-piano', '808-drums') */
    private patch = '';

    /** audio synthesizer */
    private synth = new Synthesizer();

    /** gets created by the TunePad runtime */
    private trace : MusicTrace;

    /** TunePad audio system */
    private audio : TunePadAudio;
    
    /** duration of the audio loop in beats (rounded up to nearest measure) */
    private duration = 4;

    /** datastore host url for loading custom sounds */
    private host = "https://api.tunepad.com";

    /** connectedCallback function completed? */
    private _loaded = false;


    /** audio scheduling helpers */
    get currentBeat() { return this.duration > 0 ? this.audio.beats % this.duration : this.audio.beats; }
    get cummulativeBeats() { return this.elapsedBeats + this.startBeat; }
    get beatsPerMeasure() { return this.audio.beatsPerMeasure; }
    get currentMeasure() { return Math.floor(this.currentBeat / this.beatsPerMeasure); }
    get cummulativeMeasure() { return Math.floor(this.cummulativeBeats / this.beatsPerMeasure); }
    get remainingBeats() { return this.duration - this.currentBeat; }
    get percentBeats() { return (this.currentBeat / this.duration); }
    get clockTime() { return this.audio.contextTime; }
    get startTime() { return this._start_time; }
    get startBeat() { return this._start_beat; }
    get elapsedTime() { return this.clockTime - this.startTime; }
    get beatsPerSec() { return this.audio.bpm / 60.0; }
    get secsPerBeat() { return 60.0 / this.audio.bpm;  }
    get elapsedBeats() { return this.elapsedTime * this.beatsPerSec; }
    get durationMeasures() { return Math.ceil(this.duration / this.beatsPerMeasure); }
    private _start_time = 0;
    private _start_beat = 0;
    private _last_measure = -1;


    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
        this.root.adoptedStyleSheets.push(cssDefs);
        this.root.adoptedStyleSheets.push(ContextMenuStyles);
        this.root.adoptedStyleSheets.push(styles);
        this.root.adoptedStyleSheets.push(miniTheme);
        this.root.adoptedStyleSheets.push(tutorialTheme);
        this.root.innerHTML = html;
        this.editor = document.createElement("python-editor") as PythonEditor;
        this.nameField = this.root.querySelector('.gadget-name') as HTMLInputElement;
        this.timeline = document.createElement("tunepad-timeline") as Timeline;
        this.trace = MusicTrace.emptyTrace();

        // these are both singleton objects and it's safe to call init() multiple times
        PythonRuntime.init();
        this.audio = TunePadAudio.init();
        this.audio.addSubscriber(this);
    }


    async connectedCallback() {
        this.showLoader('Loading cell data...');
        if (!this.hasAttribute('uuid')) this.setAttribute('uuid', this._uuid);

        await this.waitForWindowLoad();


        const defaultCode = this.innerHTML
            .trim()
            .split('\n')
            .map((line) => line.startsWith('/') ? line.substring(1) : line)  // ugly markdown hack
            .join('\n');


        // insert timeline
        this.root.querySelector('.timeline-wrapper')?.appendChild(this.timeline);
        if (!this.hasAttribute('timeline')) this.setTimelineView('midi');


        // insert codemirror editor
        this.editor.innerHTML = defaultCode;
        this.root.querySelector('.cell-contents')?.appendChild(this.editor);


        // initial code compile (it takes a moment to load pyodide)
        this.setHTML('#line-count', 'LOADING PYTHON...');
        this.setHTML('#uncompiled', '');

        //-------------------------
        // commenting out this line for now (11/18/2025)
        // the reason being that if cells first get connected to the DOM and then 
        // reloaded from a datastore, multiple rounds of compilation happen.
        // I think the project.recompileAll should be called instead after all of
        // the cells have loaded and precompiled.
        //-------------------------
        //await this.recompile();


        this.hideLoader();


        // listen for warnings from the compiler
        PythonRuntime.addWarningListener(this.uuid, (warns : PythonError[]) => {
            this.setClass('#warning-badge', 'hidden', warns.length === 0);
            this.editor.warnings = warns;
        });
        
        this._bindUserEvents();

        // make sure the synth is in sync
        this.synth.bpm = this.audio.bpm;

        this._loaded = true;
    }


    disconnectedCallback() {
        PythonRuntime.removeWarningListener(this.uuid);
        PythonRuntime.deleteCell(this);
        this.audio.removeSubscriber(this);
    }


    attributeChangedCallback(name : string, oldValue : string, newValue : string) {
        if (newValue === oldValue) {
            return;
        }
        else if (name === 'uuid') {
            this._uuid = newValue;
        }
        else if (name === 'name') {
            this.setName(newValue);
        }
        else if (name === 'readonly') {
            this.setReadOnly(newValue === 'true');
        }
        else if (name === 'theme') {
            this.setTheme(newValue);
        }
        else if (name === 'autocompile') {
            this.setAutoCompile(newValue === 'true');
        }
        else if (name === 'patch' && newValue) {
            this.setPatch(newValue);
        }
        else if (name === 'host') {
            this.host = newValue;
        }
        else if (name == 'font-size') {
            this.editor.setAttribute('font-size', newValue);
        }
        else if (name === 'midi-out') {
            this.setMidiOut(newValue === 'true');
        }
        else if (name === 'midi-in') {
            this.setMidiIn(newValue === 'true');
        }
        else if (name === 'saved') {
            this.setHTML('#last-saved', `Last saved: ${newValue}`);
        }
        else if (name === 'timeline') {
            this.setTimelineView(newValue);
        }
    }


    /**
     * Return a serialized snapshot of state
     */
    public save() : any {
        const data : any = { };
        this.getAttributeNames().forEach(attrib => { data[attrib] = this.getAttribute(attrib); });
        data.python = this.editor.code;
        data.type = TunePadCell.ELEMENT;
        return data;
    }

    /**
     * Restore from a saved version
     */
    public restore(data : any, loadPython : boolean = true) {
        this._preloaded = false;
        Object.entries(data).forEach(([ key, value ]) => {
            if (key !== 'python' && key !== 'type') {
                this.setAttribute(key, value as string);
            }
        });
        if (loadPython) {
            const default_code = '# add code here...\n\n\n\n\n\n\n\n\n\n';
            const code = (data.python ?? default_code)
                .split('\n')
                .map((line : string) => (line.length === 0)? '/' : line)
                .join('\n');

            this.innerHTML = code;
            if (this._loaded || this.isConnected) {
                this.editor.replaceCode(data.python ?? default_code);
            }
        }
    }


    /**
     * I'm not sure we should keep this or not. Trying to figure out how bind the codemirror editor with Y.js
     */
    public getEditor() : PythonEditor {
        return this.editor;
    }


    /**
     * Replace code in the editor
     * @param code 
     */
    public replaceCode(code : string) {
        this.editor.replaceCode(code);
    }


    /**
     * Trigger a python compile and display any errors, warnings, messages, or print outputs
     * that result.
     */
    public async recompile(force : boolean = false) {

        this.addClass('#compile-button', 'hidden');
        this.removeClass('#loader', 'hidden');
        this.disable('#play-button');
        this.disable('#stop-button');


        // compile the python code
        // preload the python module filesystem
        if (!this._preloaded) {
            await PythonRuntime.preload(this);
            this._preloaded = true;
        }

        // reactivate compile button
        this.enable('#play-button');
        this.enable('#stop-button');
        this.removeClass('#compile-button', 'hidden');
        this.addClass('#loader', 'hidden');
        this.setHTML('#uncompiled', '');

        // compile the python code
        const resp = await PythonRuntime.compile(this, force);
        await this.postCompile(resp, force);

        this.dispatchEvent(new CustomEvent("compiled", {
            bubbles: false,
            composed: true,
            detail: { uuid : this.uuid }
        }));
    }
    private _preloaded = false;


    /**
     * Run the provided python code in this cell and return the result.
     * Recompile should be called first.
     */
    public async runPythonCode(code : string) : Promise<CompileResponse> {
        const resp = await PythonRuntime.runCode(code, this);
        await this.postCompile(resp, true);
        return resp;
    }


    /**
     * Call a python event function (e.g. animate)
     */
    public async pythonEvent(event : string, args : number[] = []) : Promise<CompileResponse> {
        const code = 
        `if "${event}" in globals() and callable(globals()["${event}"]): ` +
        `${event}(${args.join(', ')})`;
        const resp = await PythonRuntime.runCode(code, this);
        this.editor.errors = resp.errors;
        this.setOutput(resp.output);
        this.setHTML('#output-badge', `${resp.output.length}`);
        this.setClass('#output-badge', 'hidden', resp.output.length === 0);
        this.setClass('#error-badge', 'hidden', resp.hasNoErrors);
        this.classList.toggle('errors', resp.hasErrors);

        // if there's an error in an event or animation callback, pause the animation loop
        if (resp.hasErrors) {
            this.pauseAudio();
        } else if (resp.trace && resp.trace.length > 0) {
            const trace = MusicTrace.fromPython(resp.trace, this.synth.patch);
            this.synth.scheduleNotes(trace, this.audio.context.destination, 0);
            if (this.midiOut) {
                MIDIManager.outputs?.forEach(port => {
                    this.synth.scheduleMidiNotes(trace, 0, port);
                });
            }
        }
        return resp;
    }


    private async postCompile(resp : CompileResponse, force : boolean) {

        // compute the new duration
        const old_trace = this.trace;
        if (resp.trace) {
            this.trace = MusicTrace.fromPython(resp.trace, this.synth.patch);
        } else {
            this.trace = MusicTrace.emptyTrace(this.synth.patch);
        }

        // load any custom sounds
        const serrors = await this.loadCustomSounds(this.trace);

        this.duration = Math.ceil(this.trace.beats / this.audio.beatsPerMeasure) * this.audio.beatsPerMeasure;

        // redraw the timeline if the trace has changed
        this.timeline.setTrace(this.trace, force);

        // show errors, messages, unit tests, and python print output
        // note that warnings can also come from an external listener set on PythonRuntime
        // combine with sound load errors and only show max 2 errors at a time...
        this.editor.errors = resp.errors.concat(serrors).slice(0, 2);

        this.setOutput(resp.output);

        /*
        this.editor.messages = this.trace.messages.map(t => toStr(t.params.get('message'), ''));
        this.editor.unitTests = this.trace.unitTests.map(t => {
            return { 
                message : toStr(t.params.get('message'), ''),
                pass : (t.params.get('type') === 'pass')
            };
        });
        */
        const warns : PythonError[] = this.trace.warnings.map(t => {
            return {
                name : t.params.get('name'),
                message : t.params.get('message'),
                details : t.params.get('details'),
                line : t.line,
                target : this.uuid,
                source : this.uuid
            };
        });

        // does this cell depend on another unsaved cell?
        for (const depends of resp.dependencies) {
            const cell = document.querySelector(`tunepad-cell[uuid="${depends}"]`);
            if (cell && (cell as TunePadCell)?.codeChanged) {
                warns.push({
                    name : 'Unsaved Changes',
                    message : `Imported cell '${cell?.getAttribute('name')}' has unsaved changes.`,
                    line : -1,
                    target : this.uuid,
                    source : this.uuid
                });
            }
        }
        this.editor.warnings = warns;

        // turn on badges
        this.setHTML('#output-badge', `${resp.output.length}`);
        this.setHTML('#fail-count', `${this.trace.fails.length}`);
        this.setHTML('#pass-count', `${this.trace.passes.length}`);
        this.setClass('#output-badge', 'hidden', resp.output.length === 0);
        this.setClass('#error-badge', 'hidden', resp.hasNoErrors);
        this.setClass('#fail-badge', 'hidden', this.trace.fails.length === 0);
        this.setClass('#pass-badge', 'hidden', this.trace.passes.length === 0);
        this.setClass('#warning-badge', 'hidden', this.trace.warnings.length === 0);

        this.classList.toggle('errors', resp.hasErrors);
        this.classList.toggle('warnings', this.trace.warnings.length > 0);

        this.codeChanged = false;

        // update line count
        this.setHTML('#line-count', `${this.editor.code.split('\n').length} LINES`);
        this.setHTML('#beat-count', `${this.duration} BEATS — `);

        // info bar and footer
        this.setHTML('#info-module', `Python (v${PythonRuntime.VERSION}) &mdash; ${this.name}`);
        this.setHTML('#info-beats', `${this.duration} beats`);
        this.setHTML('#info-time', `${this.audio.meter} time`);
        this.setHTML('#info-key', this.audio.key.name);
        this.setHTML('#info-bpm', `${this.audio.bpm} bpm`);
    }


    private setOutput(output : string[]) {
        const container = this.root.querySelector('#output-container');
        if (container) {
            container.innerHTML = '';
            if (output.length > 0) {
                const el = document.createElement('python-output');
                const out = output.join('\n').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
                el.innerHTML = out;
                container?.appendChild(el);
            }
        }
    }


    /**
     * setName can be called from the outline class to change the name of a cell
     * to a valid python identifier.
     */
    public setName(name : string) {
        if (name === this.name) return;
        const oldName = this.name;
        this.name = PythonRuntime.makePythonSafe(name, this.uuid);
        if (this._loaded) {
            PythonRuntime.renameCell(this, oldName);
        }
        this.nameField.value = this.name;
        this.setHTML('#module-name', `Module: ${this.name}`);
        this.setHTML('#cell-name-header', this.name);
        this.setAttribute('name', this.name);
    }


    private setAutoCompile(on : boolean) {
        if (on === this.autocompile) return;
        this.autocompile = on;
        this.root.querySelector('#autocomp')?.setAttribute('checked', `${on}`);
        this.setAttribute('autocompile', `${on}`);
        this.root.querySelector('#compile-button tunepad-icon')?.setAttribute('icon', on ? 'autocompile' : 'recompile');
    }

    private setReadOnly(ro : boolean) {
        this.setAttribute('readonly', `${ro}`);
        this.editor.setAttribute('readonly', `${ro}`);
        this.nameField.readOnly = ro;
        this.root.querySelector('#readonly')?.setAttribute('checked', `${ro}`);
        //this.setHTML('#readonly', ro ? '(readonly)' : '');
    }

    private setMidiOut(on : boolean) {
        if (on === this.midiOut) return;
        this.midiOut = on;
        this.setAttribute('midi-out', `${on}`);
        this.root.querySelector('tunepad-instrument')?.setAttribute('midi-out', `${on}`);
        this.root.querySelector('#midi-out')?.setAttribute('checked', `${on}`);
        if (on) MIDIManager.init();
    }

    private setMidiIn(on : boolean) {
        if (on === this.midiIn) return;
        this.midiIn = on;
        this.setAttribute('midi-in', `${on}`);
        this.root.querySelector('tunepad-instrument')?.setAttribute('midi-in', `${on}`);
        this.root.querySelector('#midi-in')?.setAttribute('checked', `${on}`);
    }

    private setTheme(theme : string) {
        if (['light', 'dark', 'bitcrush'].includes(theme)) {
            this.setAttribute('theme', theme);
            this.editor.setAttribute('theme', theme);
            this.timeline.setAttribute('theme', theme);
        }
    }

    private setTimelineView(view : string) {
        if (TimelineViews.includes(view)) {
            this.timeline.setAttribute('view', view);
            this.setAttribute('timeline', view);
            this.root.querySelector(`context-menu-item[action="timeline-${view}"]`)?.
                setAttribute('checked', 'true');
            this.root.querySelector('#timeline-mode-button tunepad-icon')?.
                setAttribute('icon', view === 'none' ? 'minus' : view);
        }
    }

    private setInstrument(instrument : string) {
        if (instrument != this._instrument) {
            this.removeClass('.cell-wrapper', this._instrument);
            this._instrument = instrument;
            this.addClass('.cell-wrapper', instrument);
            this.timeline.setAttribute('instrument', instrument);
        }
    }


    private async setPatch(patch : string) {
        if (patch === this.patch) {
            (this.root.querySelector('tunepad-instrument') as TunePadInstrument).setPatch(this.synth.patch, patch);
            return;
        }
        this.patch = patch;
        this.setAttribute('patch', patch);

        if (patch.startsWith('@')) {
            await this.synth.loadPatch(patch.substring(1));
        } else {
            const path = `${this.audio.voices}/${this.patch}/`;
            const url = new URL(path + 'patch.json', import.meta.url);
            await this.synth.loadPatch(url);
        }

        // possibly trigger timeline to be re-rendered
        this.timeline.setTrace(MusicTrace.fromTrace(this.trace, this.synth.patch));

        this.setHTML('#patch-credit', this.synth.patch.credit ?? '');
        this.setHTML('#info-instrument', this.synth.patch.name);

        if ('instrument' in this.synth.patch) {
            this.setInstrument(this.synth.patch.instrument as string);
        }

        // set the instrument patch
        (this.root.querySelector('tunepad-instrument') as TunePadInstrument)?.setPatch(this.synth.patch, patch);

        // hack to make sure that the outline icon is correct after async delay
        const outline = document.querySelector('tunepad-outline');
        if (outline) (outline as ProjectOutline).rebuildMenu();
    }


    private showLoader(message : string) {
        this.setHTML('.loader-text', message);
        this.addClass('#loader-background', 'show');
    }

    private hideLoader() {
        this.setHTML('.loader-text', '');
        this.removeClass('#loader-background', 'show');
    }

    private playNote(note : Note) {
        if (this.patch === '') return;
        this.synth.playNote(note);
        if (this.midiOut) {
            MIDIManager.outputs?.forEach(port => {
                //this.synth.setMidiProgram(port, MIDI_PROGRAM);
                this.synth.playMidiNote(note, port);
            });
        }
    }

    private releaseNote(note : Note | number) {
        this.synth.releaseNote(note);
        if (this.midiOut) {
            MIDIManager.outputs?.forEach(port => this.synth.releaseMidiNote(note, port));
        }
    }

    private instrumentAtPlayhead(ph : number) {
        this.trace
            .filter(e => e.command === TraceEvent.PLAY)
            .slice(ph)
            .forEach(e => this.instrumentNoteOn(e.note));        
    }

    private instrumentNoteOn(note : Note) {
        const el = (this.root.querySelector('tunepad-instrument') as unknown);
        if (el) { (el as TunePadInstrument).showNoteOn(note); }
    }

    private instrumentNoteOff(note : Note) {
        const el = (this.root.querySelector('tunepad-instrument') as unknown);
        if (el) { (el as TunePadInstrument).showNoteOff(note); }
    }

    private instrumentAllNotesOff() {
        const el = (this.root.querySelector('tunepad-instrument') as unknown);
        if (el) { (el as TunePadInstrument).allNotesOff(); }
    }


    private cueMeasure(measure : number, delta : number = 0) {
        const start = measure * this.beatsPerMeasure;
        const trace = this.trace.subtrace(start, start + this.beatsPerMeasure);
        delta = (measure === 0) ? delta : delta - start;
        this.synth.scheduleNotes(trace, this.audio.context.destination, delta);
        if (this.midiOut) {
            MIDIManager.outputs?.forEach(port => {
                this.synth.scheduleMidiNotes(trace, delta, port);
            });
        }
    }

    /**
     * Animate the playhead
     */
    animation() {
        if (this.audio.isPlaying(this)) {
            const ph = this.currentBeat;
            this.timeline.setAttribute('playhead', `${ph.toFixed(2)}`);
            this.instrumentAllNotesOff();
            this.instrumentAtPlayhead(ph);
            this.clearTrace();
            this.traceAtPlayhead(ph);
            if (this.hasAttribute('canvas')) {
                this.pythonEvent('animate', [ this.cummulativeBeats ]);
            }
            requestAnimationFrame((t) => { this.animation(); });
        }
    }


    /**
     * Used to cue up one measure of audio at a time ...
     */
    audioTimer = () => {
        const beat = this.cummulativeBeats;
        const measure = this.cummulativeMeasure;
        const remainingBeats = ((measure + 1) * this.beatsPerMeasure) - beat;
        const remainingTime = remainingBeats * this.secsPerBeat;
        if (measure > this._last_measure && remainingTime < 0.3) {
            this._last_measure = measure;
            this.cueMeasure((measure + 1) % this.durationMeasures, remainingBeats);
        }
        if (this._last_pulse < 0) this.pulse();
        if (this.cummulativeBeats >= this._last_pulse + 1/8) {
            this.pulse();
        }
    }
    private _timer = -1;
    private _last_pulse = -1;


    private pulse() {
        this._last_pulse = Math.floor(this.cummulativeBeats * 8) / 8;
        if (this.hasAttribute('canvas')) {
            this.pythonEvent('pulse', [ this._last_pulse ]);
        }
    }


    public playAudio() {
        this.audio.play(this);          // tell the audio engine that this cell is playing
        this._start_time = this.clockTime;
        this._start_beat = this.currentBeat;
        this._last_measure = this.currentMeasure - 1;
        this._timer = setInterval(this.audioTimer, 20);
        this.animation(); // moves the playhead
        this.cueMeasure(this.currentMeasure, this.currentMeasure * this.beatsPerMeasure - this.currentBeat);
        this.root.querySelector('#play-button')?.classList.add('hidden');
        this.root.querySelector('#pause-button')?.classList.remove('hidden');
    }


    public pauseAudio() {
        this.synth.cancelAllNotes();
        this.audio.pause(this);
        if (this._timer >= 0) clearInterval(this._timer);
        if (this.midiOut) {
            MIDIManager.outputs?.forEach(port => {
                this.synth.cancelAllMidiNotes(port);
            });
        }
        this.root.querySelector('#play-button')?.classList.remove('hidden');
        this.root.querySelector('#pause-button')?.classList.add('hidden');
    }

    public blockDropped(pointerX:number, pointerY:number, code:string) {
        this.editor.blockDropped(pointerX, pointerY, code);
        if (this.codeChanged) {
            this.emitUpdateEvent('content', this.code);
            this.emitLogEvent('block-dropped');
            this.recompile();
        }
    }

    onClockReset() { 
        this.pauseAudio();
        this.clearTrace();
        this.instrumentAllNotesOff();
        this.timeline.setAttribute('playhead', '0');
        this._last_pulse = -1;
    }
    onClockTimeChange() {
        this.timeline.setAttribute('playhead', `${this.currentBeat}`);
    }
    onTempoChange() {
        this.pauseAudio();
        this.synth.bpm = this.audio.bpm;
        this.timeline.setTrace(this.trace);
        this.setHTML('#info-bpm', `${this.audio.bpm} bpm`);
    }
    onTimeSignatureChange() {
        this.recompile(true);
    }
    onKeyChange() {
        this.setHTML('#info-key', this.audio.key.name);
    }



    /**
     * Called when the cell is first loaded into the page.
     * Bind all button clicks, codemirror events, etc.
     */
    private _bindUserEvents() {

        //---------------------------------------------------------
        // listen for codemirror editor change events
        //---------------------------------------------------------
        this.editor.addEventListener('codemirror-event', (e) => {
            switch ((e as CustomEvent).detail.event) {
                case 'lines-inserted':
                case 'lines-removed':
                    if (this.autocompile) this.recompile();
                    break;
                case 'cursor-moved':
                    if (this.autocompile && this.codeChanged) this.recompile();
                    break;
                case 'editor-blur':
                    this.emitLogEvent('editor-blur');
                    if (this.codeChanged) {
                        this.emitUpdateEvent('content', this.code);
                        if (this.autocompile) this.recompile();
                    }
                    break;
                case 'content-changed':
                    this.setHTML('#uncompiled', '*');
                    this.codeChanged = true;
                    break;
            }
        });

        //---------------------------------------------------------
        // copy the python import statement to the clipboard
        //---------------------------------------------------------
        /*
        this.root.querySelector('#copy-button')?.addEventListener('click', e => {
            navigator.clipboard.writeText(`from ${this.name} import *`);
        });
        */

        //---------------------------------------------------------
        // toggle dark / light mode
        //---------------------------------------------------------
        this.root.querySelector('#theme-button')?.addEventListener('click', e => {
            const theme = this.getAttribute('theme');
            let new_theme = 'light';
            if (theme === 'light') {
                new_theme = 'dark';
            } else if (theme == 'dark') {
                new_theme = 'bitcrush';
            } else {
                new_theme = 'light';
            }
            this.setTheme(new_theme);
            this.emitUpdateEvent('theme', new_theme);
            this.emitLogEvent('theme-button');
        });

        //---------------------------------------------------------
        // copy python code to the clipboard
        //---------------------------------------------------------
        this.root.querySelector('#copy-code-button')?.addEventListener('click', e => {
            const code = this.editor.code;
            navigator.clipboard.writeText(code);
            this.emitLogEvent('copy-code-button');
        });

        //---------------------------------------------------------        
        // context menu events
        //---------------------------------------------------------
        this.root.addEventListener('context-menu-action', (e) => {
            const action = (e as CustomEvent).detail.action;
            const checked = (e as CustomEvent).detail.checked;
            if (action === 'autocomp') {
                this.setAutoCompile(checked);
                this.emitUpdateEvent('autocompile', this.autocompile);
            }
            else if (action === 'readonly') {
                this.setReadOnly(checked);
                this.emitUpdateEvent('readonly', checked);
            }
            else if (action === 'midi-in') {
                this.setMidiIn(checked);
                this.emitUpdateEvent('midi-in', checked);
            }
            else if (action === 'midi-out') {
                this.setMidiOut(checked);
                this.emitUpdateEvent('midi-out', checked);
            }
            else if (action === 'timeline-midi') {
                this.setTimelineView('midi');
                this.emitUpdateEvent('timeline', 'midi');
            }
            else if (action === 'timeline-waveform') {
                this.setTimelineView('waveform');
                this.emitUpdateEvent('timeline', 'waveform');
            }
            else if (action === 'timeline-score') {
                this.setTimelineView('score');
                this.emitUpdateEvent('timeline', 'score');
            }
            else if (action === 'timeline-canvas') {
                this.setTimelineView('canvas');
                this.emitUpdateEvent('timeline', 'canvas');
            }
            else if (action === 'timeline-none') {
                this.setTimelineView('none');
                this.emitUpdateEvent('timeline', 'none');
            }
            else if (action === 'delete') {
                if (window.confirm('Are you sure you want to delete this cell?')) {
                    this.remove();
                    this.emitDeleteEvent();
                }
            }
            else if (action === 'copy-cell') {
                this.emitCopyEvent();
            }
            this.emitLogEvent('context-menu', { action, checked });
        });


        //---------------------------------------------------------
        // cell name field
        //---------------------------------------------------------
        this.nameField.addEventListener('blur', (e) => { 
            this.setName(this.nameField.value);
            this.emitUpdateEvent('name', this.name);
            this.emitLogEvent('name-field');
        });

        this.nameField.addEventListener('keypress', (e) => {
            if (e.key === "Enter") {
                this.nameField.blur();
            }
        });

        //---------------------------------------------------------
        // hook up our button event handlers
        //---------------------------------------------------------
        this.root.querySelector('#play-button')?.addEventListener('click', async e => {
            if (this.codeChanged) await this.recompile();
            this.playAudio();
            this.emitLogEvent('play-button');
        });
        this.root.querySelector('#pause-button')?.addEventListener('click', e => {
            this.pauseAudio();
            this.emitLogEvent('pause-button');
        });
        this.root.querySelector('#stop-button')?.addEventListener('click', e => {
            this.audio.stopAll();
            this.emitLogEvent('stop-button');
        });
        
        //---------------------------------------------------------
        // fire recompile when the compile button is pressed...
        //---------------------------------------------------------
        this.root.querySelector('#compile-button')?.addEventListener('click', e => {
            this.setHTML('#beat-count', 'COMPILING...');
            this.setHTML('#line-count', '');
            //this.state?.set('modified', Date.now());
            //this.pauseAudio();
            this.recompile(true);
            this.emitLogEvent('compile-button');
        });


        //---------------------------------------------------------
        // timeline events
        //---------------------------------------------------------
        this.timeline.addEventListener('playhead-reposition', (e) => {
            this.pauseAudio();
            this.synth.releaseAll();
            const ph = (e as CustomEvent).detail.playhead as number;
            this.audio.setTime(ph);
            this.clearTrace();
            this.traceAtPlayhead(ph);
            this.instrumentAllNotesOff();
            this.instrumentAtPlayhead(ph);
            this.emitLogEvent('playhead-reposition', { playhead : ph });
        });
        this.timeline.addEventListener('playhead-down', (e) => {
            this.pauseAudio();
            const ph = (e as CustomEvent).detail.playhead as number;
            this.clearTrace();
            this.traceAtPlayhead(ph);
            this.instrumentAllNotesOff();
            this.instrumentAtPlayhead(ph);
        });
        this.timeline.addEventListener('playhead-up', (e) => {
            this.pauseAudio();
            this.synth.releaseAll();
            this.clearTrace();
            const ph = (e as CustomEvent).detail.playhead as number;
            this.audio.setTime(ph);
        });
        this.timeline.addEventListener('note-enter', e => {
            const ph = (e as CustomEvent).detail.playhead as number;
            const t = (e as CustomEvent).detail.note as TraceEvent;
            this.clearTrace();
            this.instrumentAllNotesOff();
            this.instrumentNoteOn(t.note);
            if (t.line >= 0) this.addTrace(t.line);
        });
        this.timeline.addEventListener('note-leave', e => {
            this.clearTrace();
            this.instrumentAllNotesOff();
            this.synth.releaseAll();
        });
        this.timeline.addEventListener('note-down', e => {
            const ph = (e as CustomEvent).detail.playhead as number;
            const t = (e as CustomEvent).detail.note as TraceEvent;
            this.clearTrace();
            this.instrumentAllNotesOff();
            this.instrumentNoteOn(t.note);
            if (t.line >= 0) this.addTrace(t.line);
            this.synth.playNote(t.note);
        });
        this.timeline.addEventListener('note-up', e => {
            this.clearTrace();
            this.instrumentAllNotesOff();
            this.synth.releaseAll();
        });

        //---------------------------------------------------------
        // when the user drags the playhead, preview the audio and
        // highlight the python code that generated notes
        //---------------------------------------------------------
        let _last_slice : TraceEvent[] = [];
        this.timeline.addEventListener('playhead-drag', (evt) => {
            // current playhead location
            const ph = (evt as CustomEvent).detail.playhead as number;

            // notes intersecting playhead
            const slice = this.trace.slice(ph);

            // add trace markers
            this.clearTrace();
            this.traceAtPlayhead(ph);

            // play instrument
            this.instrumentAllNotesOff();
            this.instrumentAtPlayhead(ph);

            // release old notes
            const released = _last_slice.filter(l => slice.findIndex(e => e.id === l.id) < 0);
            released.forEach(e => { if (e.command === TraceEvent.PLAY) this.synth.releaseNote(e.note); });

            // play new notes 
            const pressed = slice.filter(e => _last_slice.findIndex(l => l.id === e.id) < 0);
            pressed.forEach(e => { if (e.command === TraceEvent.PLAY) this.synth.playNote(e.note); });

            // scrub playSounds snippets
            slice.forEach(e => {
                if (e.command === TraceEvent.SAMPLE) {
                    const start = ph - e.time;
                    const buffer = SoundLoader.getAudioBuffer(e.params.get('sound'));
                    this.synth.scrubAudio(buffer, start, start + 0.05);
                }
            })
            _last_slice = slice;
        });

        //---------------------------------------------------------
        // instrument events
        //---------------------------------------------------------
        const instrument = this.root.querySelector('tunepad-instrument');
        instrument?.addEventListener('note-on', e => {
            const { note, velocity, source } = (e as CustomEvent).detail;
            const n = new Note(note);
            n.velocity = velocity;
            this.playNote(n);
            //this.emitLogEvent('instrument-note-on', { note : n.note });
        });
        instrument?.addEventListener('note-off', e => {
            const { note, source } = (e as CustomEvent).detail;
            this.releaseNote(note);
            //this.emitLogEvent('instrument-note-off', { note : note });
        });
        instrument?.addEventListener('update', e => {
            const { property, value } = (e as CustomEvent).detail;
            if (property === 'patch') {
                this.setPatch(value);
                this.emitUpdateEvent('patch', value);
                this.emitLogEvent('patch-change', { patch : value });
            }
        });
        instrument?.addEventListener('action', e => {
            const { property, value } = (e as CustomEvent).detail;
            if (property === 'hint-insert') {
                this.editor.insertCodeInNewLine(value);
            }
        });
        instrument?.addEventListener('armed', e => {
            this.emitLogEvent('instrument-armed');
        });
        instrument?.addEventListener('disarmed', e => {
            this.emitLogEvent('instrument-disarmed');
        });

        //---------------------------------------------------------
        // info bar
        //---------------------------------------------------------
        this.root.querySelector('#info-bpm')?.addEventListener('click', e => {
            showBPMDialog(this.audio.bpm, (newTempo) => {
                this.audio.setTempo((e as CustomEvent).detail.value);
                //this.emitUpdateEvent('tempo', `${this.bpm}` );
            });
        });
        this.root.querySelector('#info-key')?.addEventListener('click', e => {
            showKeyDialog(this.audio.key.name, (key) => {
                this.audio.key = new MusicKey(key);
                //this.emitUpdateEvent('tempo', `${this.bpm}` );
            });
        });
        this.root.querySelector('#info-time')?.addEventListener('click', e => {
            showTimeDialog(this.audio.beatsPerMeasure, this.audio.beatValue, (n, d) => {
                this.audio.setTimeSignature(`${n}/${d}`);
                //this.emitUpdateEvent('tempo', `${this.bpm}` );
            });
        });
    }

    private traceAtPlayhead(ph : number) {
        const slice = this.trace.slice(ph).map(t => t.line);
        const unique = [ ... new Set(slice) ];
        unique.forEach(line => this.addTrace(line));
    }

    private addTrace(line : number) {
        this.editor.setMarker({ line }, 'trace');
    }

    private clearTrace() {
        this.editor.clearMarkers('trace');
    }

    public emitUpdateEvent(property : string, value : any) {
        const uuid = this.uuid;
        this.dispatchEvent(new CustomEvent("update-cell", {
            bubbles: false,
            composed: true,
            detail: { uuid, property, value }
        }));
    }

    private emitDeleteEvent() {
        this.dispatchEvent(new CustomEvent("delete-cell", {
            bubbles: false,
            composed: true,
            detail: { uuid : this.uuid }
        }));
    }

    private emitCopyEvent() {
        this.dispatchEvent(new CustomEvent("copy-cell", {
            bubbles: false,
            composed: true,
            detail: { uuid : this.uuid }
        }));
    }

    private emitLogEvent(event : string, data : any = { }) {
        this.dispatchEvent(new CustomEvent("cell-log", {
            bubbles: false,
            composed: true,
            detail: { event, data }
        }));
    }

    private async loadCustomSounds(trace : MusicTrace) : Promise<PythonError[]> {
        const errors : PythonError[] = [];
        for (const e of trace) {
            if (e.command === TraceEvent.SAMPLE) {
                // if this is a pre-loaded project sample, make sure it exists first
                const url = e.params.get('sound');
                if (url.startsWith('sample:')) {
                    if (!SoundLoader.hasSound(url)) {
                        errors.push({
                            name : 'Sample Error',
                            message : `Error loading sample '${url.substring(7)}'`,
                            line : e.line,
                            target : this.uuid,
                            source : this.uuid
                        });
                    }
                } else {
                    const result = await SoundLoader.loadCustomSound(url);
                    if (!result) {
                        errors.push({
                            name : 'Sample Error',
                            message : `Error loading sample '${url}'`,
                            line : e.line,
                            target : this.uuid,
                            source : this.uuid
                        });
                    }
                }
            }
        }
        return errors;
    }

    private setClass(selector : string, className : string, on : boolean) {
        on ? this.addClass(selector, className) : this.removeClass(selector, className);
    }

    private addClass(selector : string, className : string) {
        const e = this.root.querySelector(selector);
        if (e && className) {
            e.classList.add(className);
        }
    }

    private removeClass(selector : string, className : string) {
        const e = this.root.querySelector(selector);
        if (e && className) e.classList.remove(className);
    }

    private setHTML(selector : string, value : string) {
        const e = this.root.querySelector(selector);
        if (e) e.innerHTML = value;
    }

    private enable(selector : string) {
        this.root.querySelector(selector)?.removeAttribute('disabled');
    }

    private disable(selector : string) {
        this.root.querySelector(selector)?.setAttribute('disabled', 'true');
    }

    private async waitForWindowLoad() : Promise<void> {
        return new Promise((resolve) => {
            if (document.readyState === 'complete') {
                resolve();
            } else {
                window.addEventListener('load', () => {
                    resolve();
                });
            }
        });
    }
}


interface CollaborativeEditor {
    bindCollaboration(ytext : any, awareness : any) : void;
}


//const MIDI_PROGRAM = 48; // strings
//const MIDI_PROGRAM = 19; // pipe organ
//const MIDI_PROGRAM = 0; // grand piano
//const MIDI_PROGRAM = 5; // electric piano 1
//const MIDI_PROGRAM = 4; // electric piano 3

//const MIDI_PROGRAM = 16; // jazz organ
//const MIDI_PROGRAM = 18; // rock organ
//const MIDI_PROGRAM = 6; // harpsichord
//const MIDI_PROGRAM = 32; // jazz upright bass
//const MIDI_PROGRAM = 33; // electric bass
//const MIDI_PROGRAM = 11; // vibraphone
